import PropTypes from 'prop-types';
import React, { Fragment, useState } from "react";

import {
  Accordion,
  Button,
  Container,
  Checkbox,
  Grid,
  Menu,
  Segment
} from 'semantic-ui-react';

import { formatDate } from '_utils';
import { licensePoolPropType, getIsActiveAndNotExpiredLicensePool, getLicensePoolSublicensesLabel, getLicensePoolTypeLabel, getLicensePoolStatusLabel, getLicensePoolProductLabel, getLicensePoolSublicensesLabelWithPrefix } from '_utils/licensePools';

import { LICENSE_POOL_PRODUCTS, LICENSE_POOL_TYPES, STATUS, SUBLICENSE_OPTIONS_BY_MAX_VERSION } from '../../../../server/constants';

import LicensePoolInfo from './licensePoolInfo';
import CreateLicensePoolForm from '_forms/institutionLicensePoolFormCreate';
import ModifyLicensePoolCountsForm from '_forms/institutionLicensePoolFormCounts';
import ModifyLicensePoolExpiryForm from '_forms/institutionLicensePoolFormExpiry';

import LineItem from '_views/lineItem';
import LicensePoolSublicenses from './licensePoolSublicenses';
import useCanAdminLicenses from '../../../hooks/useCanAdminLicenses';

const LicensePool = ({ licensePool, onDeactivate, onModify, onUpgrade, onAddSublicense, onRemoveSublicense }) => {

  const canAdmin = useCanAdminLicenses();
  const onSubmit = (data) => onModify(licensePool._id, data);
  const onConfirmDeactivatePool = () => {
    if (window.confirm(`Deactivate the "${licensePool.name} ${licensePool.type}" license pool with id "${licensePool._id}"?`)) {
      onDeactivate(licensePool._id);
    }
  };

  const onAddSublicToPool = (sublicName) => onAddSublicense(licensePool._id, sublicName);

  const onRemoveSublicFromPool = (sublicense) => {
    if (window.confirm(`Remove the "${sublicense.name}" sublicense from the "${licensePool.name} ${licensePool.type}" license pool with id "${licensePool._id}"?`)) {
      onRemoveSublicense(licensePool._id, sublicense._id);
    }
  };

  const onConfirmUpgradePool = () => {
    if (window.confirm(`Are you sure you'd like to upgrade the "${licensePool.name} ${licensePool.type}" license pool with id "${licensePool._id}" to ${LICENSE_POOL_PRODUCTS.MAX9}?`)) {
      onUpgrade(licensePool._id);
    }
  };

  return (
    <Segment tertiary={ !getIsActiveAndNotExpiredLicensePool(licensePool) } >
      <Grid>
        <Grid.Row columns={ 2 } >
          <Grid.Column width={ 5 } >
            <LineItem label="Product" text={ [getLicensePoolProductLabel(licensePool), getLicensePoolSublicensesLabelWithPrefix(licensePool)].join(" ") } />
            <LineItem label="Type" text={ getLicensePoolTypeLabel(licensePool) } />
            <LineItem label="Created On" text={ formatDate(licensePool.created_on) } />
            <LineItem label="Status" text={ getLicensePoolStatusLabel(licensePool) } />
            <LineItem label='Description' text={ licensePool.description || "" } />
            <LineItem label='ID' text={ licensePool._id } />
            {
              canAdmin && licensePool.status === STATUS.ACTIVE ? (
                <Fragment>
                  <br/>
                  <Button negative content='Deactivate' size='mini' onClick={ onConfirmDeactivatePool } />
                  {
                    (licensePool.name === LICENSE_POOL_PRODUCTS.MAX7 || licensePool.name === LICENSE_POOL_PRODUCTS.MAX8) &&
                    licensePool.type === LICENSE_POOL_TYPES.FULL
                      ? (
                        <Button content={ 'Upgrade to ' + LICENSE_POOL_PRODUCTS.MAX9 } size='mini' onClick={ onConfirmUpgradePool } />
                      ) : null
                  }
                </Fragment>
              ) : null
            }
          </Grid.Column>
          <Grid.Column width={ 11 } >
            <Accordion
              defaultActiveIndex={ 0 }
              panels={ [

                canAdmin ? {
                  title: `License Counts`,
                  content: {
                    content: <ModifyLicensePoolCountsForm licensePool={ licensePool } onSubmit={ onSubmit } />,
                    key: `licpool_${licensePool._id}_settings`
                  }
                } : null,
                SUBLICENSE_OPTIONS_BY_MAX_VERSION[licensePool.name].length > 0 ? {
                  title: `Add Ons ${getLicensePoolSublicensesLabel(licensePool)}`,
                  content: {
                    content: <LicensePoolSublicenses
                      licensePool={ licensePool }
                      onAddSublicense={ onAddSublicToPool }
                      onRemoveSublicense={ onRemoveSublicFromPool }
                    />,
                    key: `licpool_${licensePool._id}_sublicenses`
                  }
                } : null,
                licensePool.type === LICENSE_POOL_TYPES.SUBSCRIPTION && canAdmin ? {
                  title: `Expiration: ${formatDate(licensePool.expires_on)}`,
                  content: {
                    content: <ModifyLicensePoolExpiryForm licensePool={ licensePool } onSubmit={ onSubmit } />,
                    key: `licpool_${licensePool._id}_expiry`
                  }
                } : null,
                {
                  title: `Activation Info`,
                  content: {
                    content: <LicensePoolInfo licensePool={ licensePool } />,
                    key: `licpool_${licensePool._id}_info`
                  }
                }
              ].filter(p => !!p) }
              styled
              fluid
              exclusive={ false }
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Segment>
  );
};

LicensePool.propTypes = {
  licensePool: licensePoolPropType.isRequired,
  onDeactivate: PropTypes.func.isRequired,
  onModify: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
  onAddSublicense: PropTypes.func.isRequired,
  onRemoveSublicense: PropTypes.func.isRequired
};

const POOL_VIEWS = {
  ADD: "add",
  LIST: "list"
};

const LicensePools = ({
  licensePools,
  onAddInstitutionLicensePool,
  onDeactivateInstitutionLicensePool,
  onModifyInstitutionLicensePool,
  onUpgradeInstitutionLicensePool,
  onAddSublicenseToInstitutionLicensePool,
  onRemoveSublicenseFromInstitutionLicensePool
}) => {

  const canAdmin = useCanAdminLicenses();
  const [activeView, setActiveView] = useState(POOL_VIEWS.LIST);
  const [showExpired, setShowExpired] = useState(false);

  const onCancelPoolCreate = () => setActiveView(POOL_VIEWS.LIST);
  const onSubmitPoolCreate = payload => {
    onAddInstitutionLicensePool(payload);
    setActiveView(POOL_VIEWS.LIST);
  };

  const onShowExpiredChange = (_, data) => setShowExpired(data.checked);

  return (
    <Container fluid>
      <Menu size='mini'>
        {
          canAdmin ? (
            <Menu.Item name='add_institution_pool'>
              <Button onClick={ () => setActiveView(POOL_VIEWS.ADD) } disabled={ activeView === POOL_VIEWS.ADD } >
                Add License Pool
              </Button>
            </Menu.Item>
          ) : null
        }
        <Menu.Menu position='right'>
          <Menu.Item>
            <Checkbox
              onChange={ onShowExpiredChange }
              label='Show Expired & Deactivated'
              toggle
              fitted
              size='tiny'
            />
          </Menu.Item>
        </Menu.Menu>
      </Menu>
      <Segment.Group>
        {
          activeView === POOL_VIEWS.LIST ? (
            licensePools
              .filter(pool => showExpired || getIsActiveAndNotExpiredLicensePool(pool))
              .map(pool => (
                <LicensePool
                  key={ pool._id }
                  licensePool={ pool }
                  onModify={ onModifyInstitutionLicensePool }
                  onUpgrade={ onUpgradeInstitutionLicensePool }
                  onDeactivate={ onDeactivateInstitutionLicensePool}
                  onAddSublicense={ onAddSublicenseToInstitutionLicensePool }
                  onRemoveSublicense={ onRemoveSublicenseFromInstitutionLicensePool }
                />
              ))
          ) : null
        }
        {
          activeView === POOL_VIEWS.ADD ? (
            <CreateLicensePoolForm
              onCancel={ onCancelPoolCreate }
              onSubmit={ onSubmitPoolCreate }
            />
          ) : null
        }
      </Segment.Group>
    </Container>
  );
};

LicensePools.propTypes = {
  licensePools: PropTypes.arrayOf(licensePoolPropType).isRequired,
  onAddInstitutionLicensePool: PropTypes.func.isRequired,
  onDeactivateInstitutionLicensePool: PropTypes.func.isRequired,
  onModifyInstitutionLicensePool: PropTypes.func.isRequired,
  onUpgradeInstitutionLicensePool: PropTypes.func.isRequired,
  onAddSublicenseToInstitutionLicensePool: PropTypes.func.isRequired,
  onRemoveSublicenseFromInstitutionLicensePool: PropTypes.func.isRequired
};

export default LicensePools;



// WEBPACK FOOTER //
// ./webAdmin/components/institutions/institution/licensePools.js